import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { getIconUrl, isAuthoredEmoji } from '@/lib/icons'
import { cn } from '@/lib/utils'

// Re-exported so existing importers of `isAuthoredEmoji` from this module
// (icon-field.tsx, this module's own tests) keep working. The regex itself
// lives in lib/icons.ts, which is React-free — server-side code (e.g.
// lib/notion.ts) needs the same classification without pulling in a .tsx
// component.
export { isAuthoredEmoji }

export type AuthoredIconSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl'

// The image branch gets an intrinsic size for free from `box`; the emoji
// branch has none, so without an explicit font-size the glyph would render
// at the inherited font-size instead of matching the image box. The `emoji`
// class pins that to ~91% of the box so an authored emoji doesn't render
// smaller (or larger) than a bundled image in the same slot. `chipGlyph` is
// the third ratio: a fallback glyph sitting inside a chip has to be smaller
// than the chip that contains it, at roughly half the box.
//
// This is the one module allowed to encode those ratios — everywhere else
// gets them for free through `size`. That claim used to be aspirational:
// callers passing a chip-wrapped fallback still hand-picked the glyph's
// pixels, so the box-to-glyph ratio lived in two modules. Taking the glyph as
// a component rather than as a ready-made element is what makes it true.
//
// Rungs, not a free pixel value: the point of the ladder is that call sites
// stop inventing their own 16/18/20px one-offs. Above the dense-row rung the
// steps are a regular 8px, so "the next size up" is a usable mental model.
// Add a rung only when a real surface can't sit on an existing one.
const SIZES: Record<
  AuthoredIconSize,
  { box: string; emoji: string; chipGlyph: number }
> = {
  sm: { box: 'h-3.5 w-3.5', emoji: 'text-[13px] leading-none', chipGlyph: 10 },
  md: { box: 'h-6 w-6', emoji: 'text-[22px] leading-none', chipGlyph: 14 },
  lg: { box: 'h-8 w-8', emoji: 'text-[29px] leading-none', chipGlyph: 18 },
  xl: { box: 'h-10 w-10', emoji: 'text-[36px] leading-none', chipGlyph: 20 },
  '2xl': { box: 'h-12 w-12', emoji: 'text-[44px] leading-none', chipGlyph: 24 },
}

const CHIP_CLASS = 'bg-muted flex items-center justify-center rounded'

/** The candidate that will actually be drawn, or `null` to use the fallback. */
type ResolvedIcon =
  | { kind: 'emoji'; value: string }
  | { kind: 'image'; src: string }

/**
 * Walks the candidate chain and returns the first one that can really render.
 *
 * Blank-once-trimmed values are skipped via the same rule `hasAuthoredIcon`
 * encodes — testing raw truthiness here would let `'   '` win the chain and
 * then draw a fallback, hiding the usable icon behind it. A candidate whose
 * `src` has already failed to load is skipped too, which is what makes a
 * chain a chain rather than a pick.
 */
function resolveIcon(
  candidates: ReadonlyArray<string | null | undefined>,
  failedSrcs: ReadonlySet<string>,
): ResolvedIcon | null {
  for (const candidate of candidates) {
    const value = candidate?.trim() ?? ''
    if (value === '') continue
    if (isAuthoredEmoji(value)) return { kind: 'emoji', value }
    const src = getIconUrl(value)
    if (src !== null && !failedSrcs.has(src)) return { kind: 'image', src }
  }
  return null
}

/**
 * The two fallback contracts, kept apart at the type level so a caller cannot
 * ask for both: a decorative mark this module sizes and hides, or substitute
 * content it renders untouched. See LG-331 for why they were separated.
 */
type AuthoredIconFallback =
  | {
      /** Glyph drawn when no icon renders. Sized by `size`, `aria-hidden`. */
      fallback: LucideIcon
      /** Draw the glyph on the muted rounded chip used by card surfaces. */
      fallbackChip?: boolean
      fallbackContent?: never
    }
  | {
      /** The muted rounded chip on its own, with no glyph inside it. */
      fallbackChip: true
      fallback?: never
      fallbackContent?: never
    }
  | {
      /**
       * Arbitrary substitute content — a source name, or a nested
       * `AuthoredIcon` continuing a fallback chain. Rendered verbatim: not
       * sized to the icon box, not given `className`, and not forced
       * `aria-hidden`, because it is not a decorative mark and is usually the
       * row's visible name.
       *
       * `NonNullable` because `ReactNode` admits `undefined`, which would
       * satisfy this arm and then fall through to the bare-chip branch —
       * a caller with a maybe-value has to decide what the empty case looks
       * like rather than silently getting a muted square.
       */
      fallbackContent: NonNullable<ReactNode>
      fallback?: never
      fallbackChip?: never
    }

export type AuthoredIconProps = AuthoredIconFallback & {
  /**
   * Raw `icon` column value: an emoji, an absolute URL, or a bare filename.
   *
   * An array is a *chain*, tried in order: the first candidate that is not
   * blank and can actually render wins, and one that 404s hands off to the
   * next rather than to `fallback`. Callers used to spell this as
   * `a || b || c`, which is wrong twice over — a whitespace-only value is
   * truthy, so it won the chain and then rendered as a fallback anyway,
   * skipping the real icon underneath; and `||` cannot react to a load
   * failure at all.
   */
  icon: string | null | undefined | ReadonlyArray<string | null | undefined>
  /**
   * Box size: 24px (`'md'`, the default — most callers), 14px (`'sm'`, dense
   * rows and chips), then 32/40/48px (`'lg'`/`'xl'`/`'2xl'`) for cards, list
   * rows and a modal's hero mark. Drives the image box, the matching emoji
   * font-size, the fallback mark and the glyph inside a chip, so a caller
   * never restates any of it.
   */
  size?: AuthoredIconSize
  /**
   * Caller layout extras only (e.g. `shrink-0`, colour) — sizing lives on
   * `size`. Applied to whichever *mark* renders, the fallback glyph and chip
   * included, so a caller states its layout once rather than repeating it on
   * the mark. It does not reach `fallbackContent`, which is rendered verbatim
   * and brings its own layout.
   */
  className?: string
  /**
   * What this icon stands for, for assistive tech. The mark itself is
   * decorative by contract, so something has to carry the name; this is the
   * module that decided the mark is decorative, so it owns the compensation
   * rather than leaving each caller to remember an `sr-only` span of its own.
   * Also becomes the hover `title`.
   *
   * Suppressed while `fallbackContent` is showing — that content is visible
   * text which already names the thing, and emitting both would announce the
   * name twice.
   */
  label?: string
}

/**
 * The one place that decides how an authored `icon` value becomes a picture.
 * Icons are decorative everywhere they appear, so the emoji branch is
 * `aria-hidden` and the image branch has an empty `alt` — an icon must never
 * join its row's accessible name. Pass `label` where the row does not
 * otherwise name the thing.
 *
 * Two surfaces are honest exceptions, neither of which can render a picture
 * at all:
 * - Native `<option>` labels (the task admin's source filters) hold text
 *   only, and use `authoredIconTextPrefix` from `./authored-icon-text`.
 * - `companies-list.tsx` renders `Company.imgUrl`, which is a plain image URL
 *   with no emoji authoring path — not an authored icon value.
 */
export function AuthoredIcon({
  icon,
  fallback: Fallback,
  fallbackChip = false,
  fallbackContent,
  size = 'md',
  className,
  label,
}: AuthoredIconProps): ReactNode {
  // Tracks failed *srcs* rather than a boolean, so a later icon change retries
  // instead of staying stuck on the fallback. Owned here rather than inside an
  // <img> wrapper for two reasons: `label` has to know which branch won (a
  // verbatim `fallbackContent` names itself, so the `sr-only` span must not
  // also render once the image has given up), and a chain has to advance past
  // a failed candidate to the next one rather than straight to the fallback.
  const [failedSrcs, setFailedSrcs] = useState<ReadonlySet<string>>(
    () => new Set(),
  )

  const sized = SIZES[size]
  const resolved = resolveIcon(Array.isArray(icon) ? icon : [icon], failedSrcs)

  const withLabel = (mark: ReactNode): ReactNode =>
    label === undefined ? (
      mark
    ) : (
      <>
        {mark}
        <span className="sr-only">{label}</span>
      </>
    )

  const renderFallback = (): ReactNode => {
    if (fallbackContent !== undefined) return <>{fallbackContent}</>

    if (fallbackChip || Fallback === undefined) {
      return withLabel(
        <span
          aria-hidden="true"
          title={label}
          // `box` last so the component-owned size wins over caller layout.
          className={cn(CHIP_CLASS, className, sized.box)}
        >
          {Fallback !== undefined && <Fallback size={sized.chipGlyph} />}
        </span>,
      )
    }

    // A bare glyph fills the box, so it takes the rung's classes directly
    // rather than a pixel size.
    return withLabel(
      <Fallback aria-hidden="true" className={cn(className, sized.box)} />,
    )
  }

  if (resolved === null) return renderFallback()

  if (resolved.kind === 'emoji') {
    return withLabel(
      <span
        aria-hidden="true"
        title={label}
        // Rung last on every branch, so a caller's stray `h-*`/`w-*` can
        // never defeat `size` on one branch while losing on another.
        className={cn(className, sized.box, sized.emoji)}
      >
        {resolved.value}
      </span>,
    )
  }

  const { src } = resolved

  // `loading="lazy"` matters because IconField's tabbed panels are hidden
  // rather than unmounted, so every mount would otherwise eagerly fetch all
  // ~77 bundled images. `referrerPolicy="no-referrer"` stops an absolute-URL
  // icon from leaking the admin app's URL as a referrer on every render.
  return withLabel(
    <img
      src={src}
      alt=""
      title={label}
      // `m-0` because this img is a UI mark, not prose imagery: index.css
      // styles bare `article img` with `my-8` for editorial content, and card
      // surfaces (tray/schedule cards) are semantic <article>s — without the
      // reset, a 14px source mark arrives wearing 64px of vertical margin and
      // stretches every card it sits on. `max-w-none` for the same reason:
      // preflight's `img { max-width: 100% }` is responsive-imagery insurance,
      // but on a fixed-size mark inside a shrink-to-fit container it resolves
      // the cyclic percentage to width 0 and the icon vanishes.
      className={cn(className, sized.box, 'm-0 max-w-none object-contain')}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() =>
        setFailedSrcs((prev) => {
          const next = new Set(prev)
          next.add(src)
          return next
        })
      }
    />,
  )
}
